import React, { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/pro-regular-svg-icons'
import { ModalConfirmV2, ModalSizeVariants } from '@labrav/react-components'
import { Formik, Form, FormikProps } from 'formik'
import { useParams } from 'react-router-dom'
import {
  DownloadMeteringHistoryModalContent,
  DownloadDetails,
} from './DownloadDetailsModalContent'
import { downloadMeteringHistoryValidationSchema } from '../../../../common/utils/downloadMeteringHistoryValidation'
import { useDispatch } from '../../../../store/hooks'
import {
  getAzureMeteringReportFile,
  getMeteringReportFile,
} from '../../../../modules/usageMetering/action'
import { getPreviousMonthDate } from '@labrav/common'
import { CloudMarketplace } from '../../../../modules/productsListing/types'

interface DownloadMeteringHistoryModalProps {
  open: boolean
  onClose: () => void
}
const initialDates = getPreviousMonthDate('1')

function initialCloudProviderFromRoute(routeCloud?: string): CloudMarketplace {
  const c = routeCloud?.toUpperCase()
  if (c === 'AZURE' || c === 'GCP' || c === 'AWS') {
    return c as CloudMarketplace
  }
  return 'AWS'
}

const downloadDetailsBaseInitialValues: Omit<DownloadDetails, 'cloudProvider'> =
  {
    timeFrame: '1',
    fromDate: initialDates.fromDate,
    toDate: initialDates.toDate,
    cloudProductId: '',
    productCode: '',
  }

export const DownloadMeteringHistoryModal: React.FC<
  DownloadMeteringHistoryModalProps
> = ({ open, onClose }) => {
  const dispatch = useDispatch()
  const [isDownloading, setIsDownloading] = useState(false)
  const { cloud: routeCloud } = useParams<{ cloud?: string }>()

  const formInitialValues = useMemo<DownloadDetails>(
    () => ({
      ...downloadDetailsBaseInitialValues,
      cloudProvider: initialCloudProviderFromRoute(routeCloud),
    }),
    [routeCloud]
  )

  return (
    <Formik<DownloadDetails>
      initialValues={formInitialValues}
      enableReinitialize
      validationSchema={downloadMeteringHistoryValidationSchema}
      validateOnMount={true}
      onSubmit={async values => {
        try {
          setIsDownloading(true)
          const provider = values.cloudProvider?.toUpperCase()
          if (provider === 'AZURE') {
            await dispatch(
              getAzureMeteringReportFile(
                values.fromDate || '',
                values.toDate || '',
                values.productCode || ''
              )
            )
          } else {
            await dispatch(
              getMeteringReportFile(
                values.fromDate || '',
                values.toDate || '',
                values.cloudProductId || '',
                values.cloudProvider
              )
            )
          }
          onClose()
        } catch (error) {
          console.error('Error downloading metering report:', error)
        } finally {
          setIsDownloading(false)
        }
      }}
    >
      {(formikProps: FormikProps<DownloadDetails>) => {
        const isAzure =
          formikProps.values.cloudProvider?.toUpperCase() === 'AZURE'
        const hasSelectedProduct = Boolean(formikProps.values.cloudProductId)
        const hasProductCode = Boolean(formikProps.values.productCode?.trim())
        const noMeteringDataForProduct =
          isAzure && hasSelectedProduct && !hasProductCode

        return (
          <ModalConfirmV2
            data-testid="download-metering-history-modal"
            titleAcceptButton="Download csv"
            title="Download metering history"
            icon={<FontAwesomeIcon icon={faDownload} />}
            onCloseModal={() => {
              formikProps.resetForm()
              onClose()
            }}
            onAccept={async () => {
              await formikProps.submitForm()
              formikProps.resetForm()
            }}
            scrollContent={true}
            content={
              <Form
                onSubmit={formikProps.handleSubmit}
                onReset={formikProps.handleReset}
              >
                <DownloadMeteringHistoryModalContent
                  formikProps={formikProps}
                />
              </Form>
            }
            onlyCloseModal={true}
            open={open}
            variant={ModalSizeVariants.large}
            onDecline={() => {
              formikProps.resetForm()
              onClose()
            }}
            acceptDisabled={
              isDownloading ||
              !formikProps.values.cloudProductId ||
              noMeteringDataForProduct ||
              (formikProps.values.timeFrame === 'custom' &&
                (!!formikProps.errors.fromDate ||
                  !!formikProps.errors.toDate ||
                  !formikProps.values.fromDate))
            }
            disabledAcceptButtonMessage={
              noMeteringDataForProduct
                ? 'No metering data for this product'
                : ''
            }
            acceptLoading={isDownloading}
          />
        )
      }}
    </Formik>
  )
}
